'use strict';

import _ from 'lodash';

import React from 'react';
import PropTypes from 'prop-types';

import ckActivePromo from '../data/activePromo';
import coolmathActivePromo from '../data/CoolmathPromo';
import genericPromo from '../data/genericPromo';
import PaymentPlans from './newPayments/PaymentPlans';
import getDisplayPlanIds from '../functions/getDisplayPlanIds.jsx';
import products from 'ck-core/source/data/products';
import { PLAN_SIZE } from 'ck-core/source/data/PlanSize';
import { getPlanIdForPlanType } from 'ck-core/source/data/PlanType';
import storageAffiliateKey from '../data/storageAffiliateKey';
import StorageManager from 'ck-core-client/source/managers/StorageManager';
import subscriptionPlans from 'ck-core/source/collections/subscriptionPlans';
import Frame from 'ck-core-client/source/ui/Frame';
import trialCtaPlans from '../data/trialCtaPlans';
import { FACEBOOK_CUSTOM_EVENT, trackFacebookPixel } from 'ck-core-client/source/trackers/Facebook';
import { ConnectedCheckoutStages, CHECKOUT_THEME } from '../components/CheckoutStages';
import { generateCheckoutProps } from '../pages/newPayments/CheckoutPage';
import availablePlanTypes from 'ck-core/source/functions/availablePlanTypes';

let activePromo = ckActivePromo;

if (CK.config.name === 'ck-coolmath') {
	activePromo = coolmathActivePromo;
}

export default class PaymentPlansForProduct extends React.Component {
	constructor(props) {
		super(props);

		this.state = {};

		this.checkoutStages = React.createRef();
	}

	handleSelectPlan = planId => {
		CK.trackAnalytic('SelectPlan', {
			planId
		});

		trackFacebookPixel(FACEBOOK_CUSTOM_EVENT.SELECT_PLAN, {
			planId
		});

		const trialCtaPlan = trialCtaPlans[planId];

		if (trialCtaPlan) {
			return Frame.mod().navigate(trialCtaPlan.href);
		}

		const planCursor = subscriptionPlans(planId);
		const planDuration = planCursor.durationKey();

		const checkoutQuery = _.omitBy(
			{
				planDuration,
				product: planCursor.product(),
				size: planCursor.family() ? PLAN_SIZE.FAMILY : PLAN_SIZE.INDIVIDUAL
			},
			_.isNil
		);

		const { inlineCheckoutSelection } = this.state;
		this.setState(
			prevState =>
				Object.assign({}, prevState, {
					inlineCheckoutSelection: checkoutQuery,
					inlineCheckoutPlanId: planId,
					planDuration
				}),

			() => {
				if (!inlineCheckoutSelection && typeof window !== 'undefined') {
					this.checkoutStages.current.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
						inline: 'start'
					});
				}
			}
		);
	};

	deselectPlan = () => {
		this.setState({
			inlineCheckoutPlanId: null
		});
	};

	static getDerivedStateFromProps(props, state) {
		let { product, size } = _.defaults({}, state, props);

		const disabledSizeTabs = {};

		const planIdsForSize = size => {
			const displayPlanIds = {};
			for (let product of _.values(products)) {
				const planIds = getDisplayPlanIds({
					user: CK.user,
					product,
					size
				});

				// Don't bother displaying the trial plan if none of the paid plans are available.
				if (!planIds.length) {
					displayPlanIds[product] = [];
				} else {
					displayPlanIds[product] = [..._.get(props.prefixProductPlanIds, product, []), ...planIds];
				}
			}
			return displayPlanIds;
		};

		let displayPlanIds = {};

		// Check the currently selected size first
		[size, ..._.without(Object.values(PLAN_SIZE), size)]
			.map(planSize => {
				return [planSize, planIdsForSize(planSize)];
			})
			.filter(([planSize, sizePlanIds]) => {
				const isDisabled = _.every(sizePlanIds, forProduct => forProduct.length === 0);
				if (isDisabled) {
					disabledSizeTabs[planSize] = true;
				}
				return !isDisabled;
			})
			.some(([planSize, sizePlanIds]) => {
				displayPlanIds = sizePlanIds;
				size = planSize;
				return true;
			});

		const disabledProductTabs = _.mapValues(displayPlanIds, value => !value.length);

		if (disabledProductTabs[product]) {
			product = products.BUNDLE;
		}

		const affiliate = PaymentPlansForProduct.getAffiliateFromStorage();

		return {
			product,
			size,
			disabledProductTabs,
			disabledSizeTabs,
			displayPlanIds,
			activeGenericPromo:
				affiliate && affiliate === genericPromo.affiliate ? genericPromo : undefined
		};
	}

	handleSelectProduct = product => {
		this.setState(prevState =>
			Object.assign({}, prevState, {
				product
			})
		);

		const { size, planDuration } = this.state;

		if (this.props.handleSelectProduct) {
			this.props.handleSelectProduct(product);
		}
		if (size && planDuration) {
			const selectedType = {
				size,
				duration: planDuration,
				product
			};
			const canPurchasePlan = availablePlanTypes(CK.user.currentPlanType()).some(planType =>
				_.isEqual(planType, selectedType)
			);
			if (canPurchasePlan) {
				this.handleSelectPlan(getPlanIdForPlanType(selectedType));
			} else {
				this.deselectPlan();
			}
		}
	};

	handleSelectSize = size => {
		this.setState(prevState =>
			Object.assign({}, prevState, {
				size
			})
		);

		if (this.props.handleSelectSize) {
			this.props.handleSelectSize(size);
		}

		const { product, planDuration } = this.state;

		if (product && planDuration) {
			this.handleSelectPlan(
				getPlanIdForPlanType({
					size,
					duration: planDuration,
					product
				})
			);
		}
	};

	static getAffiliateFromStorage() {
		const storageManager = StorageManager.mod();
		return storageManager ? storageManager.get(storageAffiliateKey) : undefined;
	}

	render() {
		const {
			disabledSizeTabs,
			disabledProductTabs,
			size,
			product,
			displayPlanIds,
			activeGenericPromo,
			inlineCheckoutPlanId,
			inlineCheckoutSelection
		} = this.state;

		const promo =
			(activePromo && activePromo.getProps()) ||
			(activeGenericPromo && activeGenericPromo.getProps());

		return (
			<React.Fragment>
				<PaymentPlans
					showSizeTabs={true}
					disabledSizeTabs={disabledSizeTabs}
					activeSizeTab={size}
					handleSelectSize={this.handleSelectSize}
					showProductTabs={this.props.showProductTabs}
					disabledProductTabs={disabledProductTabs}
					activeProductTab={product}
					handleSelectProduct={this.handleSelectProduct}
					promo={promo}
					planIds={displayPlanIds[product] || []}
					handleSelectPlan={this.handleSelectPlan}
					highlightedPlan={inlineCheckoutPlanId}
					inlineCheckoutStages={
						inlineCheckoutPlanId && (
							<div ref={this.checkoutStages}>
								<ConnectedCheckoutStages
									allowStageNavigation={false}
									theme={CHECKOUT_THEME.COMPACT}
									showGiftCheckbox={false}
									showFamilyCheckbox={false}
									showGuaranteesBanner={true}
									handleSelectPlan={this.handleSelectPlan}
									{...generateCheckoutProps(inlineCheckoutSelection)}
								/>
							</div>
						)
					}
				/>
			</React.Fragment>
		);
	}
}

PaymentPlansForProduct.defaultProps = {
	size: PLAN_SIZE.INDIVIDUAL,
	showProductTabs: true
};

PaymentPlansForProduct.propTypes = {
	product: PropTypes.oneOf(_.values(products)).isRequired,
	size: PropTypes.oneOf(_.values(PLAN_SIZE)),
	handleSelectSize: PropTypes.func,
	handleSelectProduct: PropTypes.func,
	showProductTabs: PropTypes.bool,
	prefixProductPlanIds: PropTypes.shape(
		_.zipObject(
			_.values(products),
			_.times(_.values(products).length, _.constant(PropTypes.arrayOf(PropTypes.string)))
		)
	)
};
